export function canUseDOM() {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  );
}

export const isBrowser = canUseDOM();

/**
 * Get an element's owner document. Useful when components are used in iframes
 * or other environments like dev tools.
 */
export function getOwnerDocument<T extends HTMLElement = HTMLElement>(
  element: T | null
) {
  return element && element.ownerDocument
    ? element.ownerDocument
    : canUseDOM()
    ? document
    : null;
}

export function getOwnerWindow<T extends HTMLElement = HTMLElement>(
  element: T | null
) {
  const doc = element ? getOwnerDocument(element) : null;
  return doc ? doc.defaultView || window : null;
}

/**
 * Get the size of the working document minus the scrollbar offset.
 */
export function getDocumentDimensions(
  element?: HTMLElement | null | undefined
) {
  if (!canUseDOM()) return { width: 0, height: 0 };

  const doc = element ? getOwnerDocument(element)! : document;
  const win = element ? getOwnerWindow(element)! : window;

  return {
    width: doc.documentElement.clientWidth || win.innerWidth,
    height: doc.documentElement.clientHeight || win.innerHeight,
  };
}

/**
 * ARIA-attributes are generally `boolean` attributes — the presence
 * of the attribute itself is considered `true` even if the value is
 * `false`. This sets the value to `undefined` if false, so the
 * attribute won't render.
 */
export function ariaAttr(condition: boolean | string | undefined) {
  return condition ? true : undefined;
}
